import * as React from 'react';
import {
  Button,
  CameraFeed,
  ScanFrame,
  type CameraFeedState,
} from '@relay-towing/ui';
import { QrCode } from 'lucide-react';
import { useQrScan } from '../../hooks/useQrScan';
import { canScanQr, isNative, scanQr } from '../../lib/nativeBridge';
import { cameraNotice } from './CameraNotice';

/**
 * Scan a QR code, the one way the shell does it: in a wrapper the phone's
 * own scanner, through the bridge; in a browser that can open a camera, the
 * library's camera in a ScanFrame, read on the page. With neither, there is
 * no button. `onCode` gets the code either way, the same string. It goes
 * in a list's toolbar, beside the search it fills in.
 */
export function ScanQrButton({
  label,
  hint,
  onCode,
}: {
  /** The button's name, saying what is scanned. */
  label: string;
  /** The line over the guide box, in a browser. */
  hint: string;
  onCode: (code: string) => void;
}) {
  const [open, setOpen] = React.useState(false);
  if (!canScanQr()) return null;

  const scan = async () => {
    if (!isNative()) {
      setOpen(true);
      return;
    }
    const code = await scanQr();
    if (code) onCode(code);
  };

  return (
    <>
      {/* Drawn as the list toolbar's magnifier beside it: a scan is a
          search, filled in by the camera. */}
      <Button
        variant="ghost"
        size="icon-lg-glyph"
        aria-label={label}
        onClick={scan}
      >
        <QrCode />
      </Button>
      {open ? (
        <QrCamera
          hint={hint}
          onClose={() => setOpen(false)}
          onCode={(code) => {
            setOpen(false);
            onCode(code);
          }}
        />
      ) : null}
    </>
  );
}

/** The browser's scanner: the camera, read frame by frame until a code shows. */
function QrCamera({
  hint,
  onCode,
  onClose,
}: {
  hint: string;
  onCode: (code: string) => void;
  onClose: () => void;
}) {
  const video = React.useRef<HTMLVideoElement>(null);
  const [camera, setCamera] = React.useState<CameraFeedState>('starting');
  const { failed } = useQrScan(video, onCode, camera === 'live');
  return (
    <ScanFrame
      label="Scan QR code"
      shape="square"
      hint={hint}
      feed={<CameraFeed ref={video} onStateChange={setCamera} />}
      notice={cameraNotice(failed ? 'unreadable' : camera)}
      onClose={onClose}
    />
  );
}
